import PropTypes from 'prop-types'
import ModalComponent from 'components/common/ModalComponent'

// Palette cycled per distinct sector/block.
const BLOCK_COLORS = ['#2563eb', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#dc2626']

// 'HH:MM' -> minutes since midnight (null when missing/invalid).
const toMin = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string') return null
  const [h, m] = hhmm.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return null
  return h * 60 + m
}

const AttendanceTimeline = ({ worker }) => {
  const segments = Array.isArray(worker.segments) ? worker.segments : []

  const entry = toMin(worker.entryTime)
  const exit = toMin(worker.officialEndTime)
  const breakStart = toMin(worker.breakStart)
  const breakEnd = toMin(worker.breakEnd)

  // Axis bounds: cover the attendance window and every segment, with padding.
  const times = [entry, exit]
  segments.forEach(s => { times.push(toMin(s.segStart), toMin(s.segEnd)) })
  const valid = times.filter(t => t != null)
  if (valid.length === 0) {
    return <p className='text-sm text-gray-500'>No time data for this worker.</p>
  }
  let axisMin = Math.min(...valid)
  let axisMax = Math.max(...valid)
  if (axisMax - axisMin < 60) axisMax = axisMin + 60 // guarantee a drawable span
  axisMin = Math.floor(axisMin / 30) * 30
  axisMax = Math.ceil(axisMax / 30) * 30

  const W = 1000
  const padL = 20
  const padR = 20
  const plotW = W - padL - padR
  const x = (min) => padL + ((min - axisMin) / (axisMax - axisMin)) * plotW
  const fmt = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`

  const barTop = 88
  const barH = 54
  const barBottom = barTop + barH

  // Does any sector span the break? Then block_active_at resets to break end.
  const hasBreak = breakStart != null && breakEnd != null && breakEnd > breakStart
  const resets = hasBreak && segments.some(s => {
    const ss = toMin(s.segStart)
    const se = toMin(s.segEnd)
    return ss != null && se != null && ss < breakStart && se > breakEnd
  })

  // Individual scans, clustered by minute (a burst of scans in the same minute
  // becomes one dot with a count) so the marks stay readable.
  const scanCounts = new Map()
  segments.forEach(s => (Array.isArray(s.scans) ? s.scans : []).forEach(t => {
    const m = toMin(t)
    if (m != null) scanCounts.set(m, (scanCounts.get(m) || 0) + 1)
  }))
  const scanClusters = Array.from(scanCounts.entries()).map(([min, count]) => ({ min, count }))
  const scanY = barTop - 18 // scan markers sit in a lane above the segment bars

  // Hour ticks across the axis.
  const ticks = []
  for (let t = Math.ceil(axisMin / 60) * 60; t <= axisMax; t += 60) ticks.push(t)

  return (
    <svg viewBox={`0 56 ${W} 150`} className='w-full' role='img' aria-label='Worker timeline'>
      <defs>
        <pattern id='wt-hatch' width='8' height='8' patternTransform='rotate(45)' patternUnits='userSpaceOnUse'>
          <rect width='8' height='8' fill='#e5e7eb' />
          <line x1='0' y1='0' x2='0' y2='8' stroke='#9ca3af' strokeWidth='3' />
        </pattern>
      </defs>

      {/* gridlines + axis */}
      {ticks.map(t => (
        <line key={`g${t}`} x1={x(t)} y1={64} x2={x(t)} y2={barBottom + 12} stroke='#eef0f3' strokeWidth='1' />
      ))}
      <line x1={padL} y1={barBottom + 12} x2={W - padR} y2={barBottom + 12} stroke='#d1d5db' strokeWidth='1.5' />

      {/* attendance window (faint) */}
      {entry != null && exit != null && (
        <rect x={x(entry)} y={barTop} width={Math.max(0, x(exit) - x(entry))} height={barH} rx='5' fill='#f9fafb' stroke='#e5e7eb' />
      )}

      {/* sector segments */}
      {segments.map((s, i) => {
        const ss = toMin(s.segStart)
        const se = toMin(s.segEnd)
        if (ss == null || se == null) return null
        const w = Math.max(2, x(se) - x(ss))
        const cx = x(ss) + w / 2
        const color = BLOCK_COLORS[i % BLOCK_COLORS.length]
        return (
          <g key={`${s.blockName}-${i}`}>
            <rect x={x(ss)} y={barTop} width={w} height={barH} rx='4' fill={color} />
            {w > 44 && (
              <>
                <text x={cx} y={barTop + 24} fontSize='15' fontWeight='700' fill='#fff' textAnchor='middle'>{s.blockName}</text>
                <text x={cx} y={barTop + 42} fontSize='12' fill='#ffffffcc' textAnchor='middle'>{Number(s.hours).toFixed(2)} h</text>
              </>
            )}
          </g>
        )
      })}

      {/* break overlay */}
      {hasBreak && (
        <g>
          <rect x={x(breakStart)} y={barTop} width={Math.max(2, x(breakEnd) - x(breakStart))} height={barH} fill='url(#wt-hatch)' stroke='#cbd5e1' />
          <text x={(x(breakStart) + x(breakEnd)) / 2} y={barTop + 30} fontSize='10' fontWeight='700' fill='#4b5563' textAnchor='middle'>BREAK</text>
        </g>
      )}

      {/* Individual scan markers, in a lane above the bars. Workers scan lines in
          bursts seconds apart, so same-minute scans collapse into one marker with
          the SCAN COUNT inside it — every individual scan is accounted for
          without overlapping labels. Hover shows the time and count. */}
      {scanClusters.map(({ min, count }) => (
        <g key={`sc${min}`}>
          <line x1={x(min)} y1={scanY + 4} x2={x(min)} y2={barTop} stroke='#c7d2e8' strokeWidth='1' />
          {count > 1
            ? (
              <>
                <circle cx={x(min)} cy={scanY} r='7.5' fill='#ffffff' stroke='#1e3a8a' strokeWidth='1.6' />
                <text x={x(min)} y={scanY + 3} fontSize='8.5' fontWeight='700' fill='#1e3a8a' textAnchor='middle'>{count}</text>
              </>
              )
            : <circle cx={x(min)} cy={scanY} r='4' fill='#ffffff' stroke='#1e3a8a' strokeWidth='1.6' />}
          <title>{fmt(min)}{count > 1 ? ` — ${count} scans` : ''}</title>
        </g>
      ))}

      {/* block_active_at reset marker (below the bar) */}
      {resets && (
        <g>
          <line x1={x(breakEnd)} y1={barBottom} x2={x(breakEnd)} y2={barBottom + 30} stroke='#dc2626' strokeWidth='1.3' strokeDasharray='3 3' />
          <text x={x(breakEnd)} y={barBottom + 44} fontSize='10.5' fontWeight='700' fill='#b91c1c' textAnchor='middle'>block_active_at → {fmt(breakEnd)}</text>
        </g>
      )}

      {/* clock-in flag */}
      {entry != null && (
        <>
          <circle cx={x(entry)} cy={barTop} r='3' fill='#111827' />
          <text x={x(entry) + 6} y={barTop - 4} fontSize='11' fontWeight='600' fill='#111827' textAnchor='start'>Clock-in {fmt(entry)}</text>
        </>
      )}

      {/* time ticks */}
      {ticks.map(t => (
        <text key={`t${t}`} x={x(t)} y={barBottom + 30} fontSize='11.5' fill='#6b7280' textAnchor='middle'>{fmt(t)}</text>
      ))}
    </svg>
  )
}

// Lane colors for the per-activity chart. The workday lane color is kept out of
// this list so activity lanes never look like the workday bar.
const ACTIVITY_COLORS = ['#7c3aed', '#e11d48', '#d97706', '#2563eb', '#65a30d', '#db2777', '#92400e', '#475569']
const WORKDAY_COLOR = '#0891b2'

// PickApp-style chart: one lane per (activity, block) with its raw scan window
// (first scan -> last scan) and its wall-clock duration, plus a bottom lane for
// the attendance window ("Workday"). `capTime` clamps the workday lane when the
// selected day is still in progress ('HH:MM' of now, or null for past days).
const ActivityTimeline = ({ worker, capTime }) => {
  const activities = Array.isArray(worker.activities) ? worker.activities : []
  const entry = toMin(worker.entryTime)
  const officialEnd = toMin(worker.officialEndTime)
  const cap = toMin(capTime)
  const workdayEnd = officialEnd != null && cap != null ? Math.min(officialEnd, cap) : officialEnd

  const times = [entry, workdayEnd]
  activities.forEach(a => { times.push(toMin(a.firstScan), toMin(a.lastScan)) })
  const valid = times.filter(t => t != null)
  if (valid.length === 0) return null
  let axisMin = Math.min(...valid)
  let axisMax = Math.max(...valid)
  if (axisMax - axisMin < 60) axisMax = axisMin + 60
  axisMin = Math.floor(axisMin / 30) * 30
  axisMax = Math.ceil(axisMax / 30) * 30

  const W = 1000
  const padL = 20
  const padR = 20
  const plotW = W - padL - padR
  const x = (min) => padL + ((min - axisMin) / (axisMax - axisMin)) * plotW
  const fmt = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`

  const laneH = 26
  const laneGap = 14
  const topPad = 16
  const lanes = activities.length + (entry != null && workdayEnd != null ? 1 : 0)
  const axisY = topPad + lanes * (laneH + laneGap) + 6
  const H = axisY + 34

  const ticks = []
  for (let t = Math.ceil(axisMin / 60) * 60; t <= axisMax; t += 60) ticks.push(t)

  const lane = (key, a, b, color, label, laneIndex) => {
    const y = topPad + laneIndex * (laneH + laneGap)
    const w = Math.max(4, x(b) - x(a))
    const wide = w > 90
    return (
      <g key={key}>
        <rect x={x(a)} y={y} width={w} height={laneH} rx='5' fill={color} />
        {wide
          ? <text x={x(a) + w / 2} y={y + laneH / 2 + 4} fontSize='11.5' fontWeight='700' fill='#ffffff' textAnchor='middle'>{label}</text>
          : <text x={x(a) + w + 6} y={y + laneH / 2 + 4} fontSize='11' fontWeight='600' fill='#374151' textAnchor='start'>{label}</text>}
        <title>{fmt(a)} – {fmt(b)}</title>
      </g>
    )
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className='w-full' role='img' aria-label='Activity timeline'>
      {ticks.map(t => (
        <line key={`ag${t}`} x1={x(t)} y1={topPad - 6} x2={x(t)} y2={axisY} stroke='#eef0f3' strokeWidth='1' />
      ))}
      <line x1={padL} y1={axisY} x2={W - padR} y2={axisY} stroke='#d1d5db' strokeWidth='1.5' />

      {activities.map((a, i) => {
        const fa = toMin(a.firstScan)
        const la = toMin(a.lastScan)
        if (fa == null || la == null) return null
        return lane(`act${i}`, fa, la, ACTIVITY_COLORS[i % ACTIVITY_COLORS.length], `${Number(a.hours).toFixed(2)} h`, i)
      })}

      {entry != null && workdayEnd != null && lane(
        'workday', entry, workdayEnd, WORKDAY_COLOR,
        `${((workdayEnd - entry) / 60).toFixed(2)} h`, activities.length
      )}

      {ticks.map(t => (
        <text key={`at${t}`} x={x(t)} y={axisY + 20} fontSize='11.5' fill='#6b7280' textAnchor='middle'>{fmt(t)}</text>
      ))}
    </svg>
  )
}

const ActivityLegend = ({ worker }) => {
  const activities = Array.isArray(worker.activities) ? worker.activities : []
  return (
    <div className='flex flex-wrap gap-x-5 gap-y-1.5 pt-2 border-t border-gray-100'>
      {activities.map((a, i) => (
        <span key={`lg${i}`} className='flex items-center gap-1.5 text-xs text-gray-600'>
          <span className='inline-block w-3 h-3 rounded-sm' style={{ backgroundColor: ACTIVITY_COLORS[i % ACTIVITY_COLORS.length] }} />
          {a.activityName}, {a.blockName}{a.cropName ? `, ${a.cropName}` : ''}
        </span>
      ))}
      <span className='flex items-center gap-1.5 text-xs text-gray-600'>
        <span className='inline-block w-3 h-3 rounded-sm' style={{ backgroundColor: WORKDAY_COLOR }} />
        Workday
      </span>
    </div>
  )
}

const WorkerTimelineModal = ({ isOpen, onClose, worker, capTime = null }) => {
  if (!worker) return null
  const title = `Timeline — ${worker.workerName || ''}`
  const hasActivities = Array.isArray(worker.activities) && worker.activities.length > 0
  return (
    <ModalComponent isOpen={isOpen} onClose={onClose} title={title} hideDefaultFooter maxWidth='screen'>
      <div className='space-y-4'>
        <div className='flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600'>
          <span><span className='text-gray-400'>ID:</span> {worker.oldId || worker.workerId}</span>
          <span><span className='text-gray-400'>Group:</span> {worker.groupName || '—'}</span>
          <span><span className='text-gray-400'>Clock-in:</span> {worker.entryTime || '—'}</span>
          <span><span className='text-gray-400'>Official end:</span> {worker.officialEndTime || '—'}</span>
          <span><span className='text-gray-400'>Total:</span> <b className='text-gray-800'>{worker.totalHours} h</b></span>
        </div>

        {(!worker.segments || worker.segments.length === 0) && (
          <p className='text-sm text-amber-600'>No agro scans this day — showing the attendance window only.</p>
        )}

        <div className='rounded-lg border border-gray-200 p-3'>
          <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>Sector timeline</p>
          <AttendanceTimeline worker={worker} />
        </div>

        {hasActivities && (
          <div className='rounded-lg border border-gray-200 p-3'>
            <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>Activity timeline</p>
            <ActivityTimeline worker={worker} capTime={capTime} />
            <ActivityLegend worker={worker} />
          </div>
        )}
      </div>
    </ModalComponent>
  )
}

const segmentShape = PropTypes.shape({
  blockName: PropTypes.string,
  firstScan: PropTypes.string,
  lastScan: PropTypes.string,
  segStart: PropTypes.string,
  segEnd: PropTypes.string,
  blockActiveAt: PropTypes.string,
  scans: PropTypes.arrayOf(PropTypes.string),
  hours: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
})

const activityShape = PropTypes.shape({
  activityName: PropTypes.string,
  blockName: PropTypes.string,
  cropName: PropTypes.string,
  firstScan: PropTypes.string,
  lastScan: PropTypes.string,
  hours: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
})

const workerShape = PropTypes.shape({
  workerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  oldId: PropTypes.string,
  workerName: PropTypes.string,
  groupName: PropTypes.string,
  entryTime: PropTypes.string,
  officialEndTime: PropTypes.string,
  breakStart: PropTypes.string,
  breakEnd: PropTypes.string,
  totalHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  segments: PropTypes.arrayOf(segmentShape),
  activities: PropTypes.arrayOf(activityShape)
})

AttendanceTimeline.propTypes = { worker: workerShape.isRequired }
ActivityTimeline.propTypes = { worker: workerShape.isRequired, capTime: PropTypes.string }
ActivityLegend.propTypes = { worker: workerShape.isRequired }

WorkerTimelineModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  worker: workerShape,
  capTime: PropTypes.string
}

export default WorkerTimelineModal
